import React, { useState, useEffect, useCallback } from 'react';
import { getLeaderboard, getDepartmentStats } from '../services/appreciationService';

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [departmentStats, setDepartmentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [viewMode, setViewMode] = useState('leaderboard'); // 'leaderboard' or 'departments'

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const dept = selectedDepartment === 'all' ? null : selectedDepartment;
      const [leaderboardData, statsData] = await Promise.all([
        getLeaderboard(dept),
        getDepartmentStats()
      ]);

      setLeaderboard(leaderboardData);
      setDepartmentStats(statsData);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getRankEmoji = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `#${index + 1}`;
  };

  const showDepartments = departmentStats && departmentStats.has_departments;

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Leaderboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          See who's being recognized the most in your organization.
        </p>
      </div>

      {/* View Mode Tabs (only show if departments exist) */}
      {showDepartments && (
        <div className="flex border-b mb-6">
          <button
            className={`py-2 px-4 ${viewMode === 'leaderboard' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            onClick={() => setViewMode('leaderboard')}
          >
            Leaderboard
          </button>
          <button
            className={`py-2 px-4 ml-4 ${viewMode === 'departments' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            onClick={() => setViewMode('departments')}
          >
            By Department
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : (
        <>
          {/* Leaderboard View */}
          {viewMode === 'leaderboard' && (
            <>
              {/* Department Filter (only show if departments exist) */}
              {showDepartments && (
                <div className="mb-6 flex justify-end">
                  <select
                    className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                  >
                    <option value="all">All Departments</option>
                    {departmentStats.departments.map((dept) => (
                      <option key={dept.department} value={dept.department}>
                        {dept.department}
                      </option>
                    ))}
                    {departmentStats.unassigned_user_count > 0 && (
                      <option value="unassigned">Unassigned</option>
                    )}
                  </select>
                </div>
              )}

              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      {showDepartments && selectedDepartment === 'all' && (
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Department
                        </th>
                      )}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Appreciations
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leaderboard.length > 0 ? (
                      leaderboard.map((user, index) => (
                        <tr key={user.user_id} className={index < 3 ? 'bg-blue-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <span className="text-xl">{getRankEmoji(index)}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                                {user.name.charAt(0)}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              </div>
                            </div>
                          </td>
                          {showDepartments && selectedDepartment === 'all' && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.department || <span className="text-gray-400 italic">Unassigned</span>}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full">
                              {user.received_count}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                          No appreciations yet. Be the first to send one!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Departments Comparison View */}
          {viewMode === 'departments' && showDepartments && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">Department Comparison</h2>
                <p className="text-sm text-gray-600 mb-6">
                  See how departments stack up in appreciation culture
                </p>

                <div className="space-y-4">
                  {departmentStats.departments.map((dept, index) => {
                    const maxAppreciations = Math.max(...departmentStats.departments.map(d => d.appreciation_count));
                    const percentage = maxAppreciations > 0
                      ? (dept.appreciation_count / maxAppreciations) * 100
                      : 0;

                    return (
                      <div key={dept.department} className="border-b pb-4 last:border-b-0">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : ''}
                            </span>
                            <div>
                              <h3 className="font-semibold text-lg">{dept.department}</h3>
                              <p className="text-sm text-gray-500">{dept.user_count} team members</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-blue-600">{dept.appreciation_count}</div>
                            <div className="text-xs text-gray-500">appreciations</div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Leaderboard;
